import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFulfiller } from '../FulfillerContext';
import { useSnackbar } from '../SnackbarContext';
import {
  BreadcrumbItem,
  Breadcrumbs,
  Button,
  NavTab,
  NavTabItem,
  Spinner,
} from '@cimpress/react-components';
import { LandingPage } from './LandingPage';
import GeneralTab from '../components/GeneralTab';
import AttributesTab from '../components/AttributesTab';
import StagesTab from '../components/StagesTab';
import RelationshipsTab from '../components/RelationshipsTab';
import '../css/common.css';
import styles from '../css/pages.module.scss';
import { CreateEntityTypeRequest, EntityType } from '@/models/EntityType';
import { createEntityType } from '../services/entity.management.service';
import { CreateEntityTypeTab } from '../constants';
import type {
  GeneralTabFormData,
  Attribute,
  EntityTypeAttributes,
  Stage,
  Relationship,
} from '../models/EditableList';
import { convertToEntityTypeAttributes, generateId } from '@/utilities/common';
import { validateEntityTypeForm } from '@/services/common.service';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';

const CreateEntityTypePage = () => {
  const navigate = useNavigate();
  const { fulfillerData, refreshEntityTypes } = useFulfiller();
  const { showSnackbar } = useSnackbar();
  const [selectedTab, setSelectedTab] = useState<CreateEntityTypeTab>(
    CreateEntityTypeTab.GENERAL,
  );
  //if user doesn't have permission, we can redirect them to dashboard
  const permission = useSelector((state: RootState) => state.permission);
  useEffect(() => {
    if (!permission.create) {
      navigate(
        `/entityTypes?tenantId=${fulfillerData?.fulfillerLocation.value}`,
      );
    }
  }, [permission, navigate, fulfillerData]);

  const [formData, setFormData] = useState<GeneralTabFormData>({
    entityTypeName: '',
    tags: [],
    description: '',
  });
  const [attributes, setAttributes] = useState<Attribute[]>([]);
  const [stages, setStages] = useState<Stage[]>([
    { id: generateId('stage'), stage: '', status: 'created' },
    { id: generateId('stage'), stage: '', status: 'completed' },
  ]);
  const [relationships, setRelationships] = useState<Relationship[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    const tenantId = fulfillerData?.fulfillerLocation.value;

    if (!tenantId) {
      showSnackbar('Tenant ID is not available', 'danger');
      return;
    }

    const validation = validateEntityTypeForm(formData, stages, relationships);

    if (!validation.isValid) {
      showSnackbar(validation.error!, 'danger');
      return;
    }

    const payload: CreateEntityTypeRequest = {
      ...formData,
      tags: formData.tags.length > 0 ? formData.tags : undefined,
      entityTypeAttributes: getEntityTypeAttributes() ?? {
        type: 'object',
        properties: {},
      },
      statusMapping:
        stages.filter((stage) => stage.stage?.trim()).length > 0
          ? stages
              .filter((stage) => stage.stage?.trim())
              .map((stage) => ({
                stage: stage.stage,
                status: stage.status,
              }))
          : undefined,
      entityTypeRelationships:
        relationships.length > 0
          ? relationships.map((rel) => ({
              relatedEntityTypeId: rel.entityTypeId,
            }))
          : undefined,
    };

    try {
      setIsSubmitting(true);
      const response: EntityType = await createEntityType(tenantId, payload);
      showSnackbar('Entity Type created successfully', 'success');

      navigate(`/entitytypes/${response.entityTypeId}?tenantId=${tenantId}`);
      refreshEntityTypes();
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      showSnackbar('Failed to create entity type.', 'danger');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getEntityTypeAttributes = (): EntityTypeAttributes | null => {
    return convertToEntityTypeAttributes(attributes);
  };

  if (!fulfillerData) {
    return <LandingPage />;
  }

  if (!fulfillerData.fulfillerLocation.value) {
    return <Spinner />;
  }

  // Tab content - add your components here
  let tabContent = <div>None</div>;
  switch (selectedTab) {
    case CreateEntityTypeTab.GENERAL:
      tabContent = <GeneralTab formData={formData} onChange={setFormData} />;
      break;
    case CreateEntityTypeTab.ATTRIBUTES:
      tabContent = (
        <AttributesTab attributes={attributes} onChange={setAttributes} />
      );
      break;
    case CreateEntityTypeTab.STAGES:
      tabContent = <StagesTab stages={stages} onChange={setStages} />;
      break;
    case CreateEntityTypeTab.RELATIONSHIPS:
      tabContent = (
        <RelationshipsTab
          relationships={relationships}
          onChange={setRelationships}
        />
      );
      break;
    default:
      tabContent = <div>{/* Default content */}</div>;
  }

  return (
    <div className={styles.entityTypePage}>
      <Breadcrumbs className="breadcrumbs">
        <BreadcrumbItem className="breadcrumb-item">
          <Button
            variant="anchor"
            onClick={() =>
              navigate(
                `/entitytypes?=tenantId=/${fulfillerData.fulfillerLocation.value}`,
              )
            }
          >
            <div className="breadcrumb-item-text-link">Entity Types</div>
          </Button>
        </BreadcrumbItem>
        <BreadcrumbItem className="breadcrumb-item">
          <div className="breadcrumb-item-text">Create Entity Type</div>
        </BreadcrumbItem>
      </Breadcrumbs>

      <div className={styles.entityTypeTopRow}>
        <div className={styles.entityTypeNameContainer}>
          <h1>Create Entity Type</h1>
        </div>
        <div>
          <Button
            className={styles.entityTypeDeleteButton}
            variant="secondary"
            onClick={() =>
              navigate(
                `/entitytypes?tenantId=${fulfillerData.fulfillerLocation.value}`,
              )
            }
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      <div className={styles.entityTypeNavTabContainer}>
        <NavTab style={{ marginBottom: 0 }}>
          <NavTabItem active={selectedTab === CreateEntityTypeTab.GENERAL}>
            <button onClick={() => setSelectedTab(CreateEntityTypeTab.GENERAL)}>
              General
            </button>
          </NavTabItem>
          <NavTabItem active={selectedTab === CreateEntityTypeTab.ATTRIBUTES}>
            <button
              onClick={() => setSelectedTab(CreateEntityTypeTab.ATTRIBUTES)}
            >
              Attributes
            </button>
          </NavTabItem>
          <NavTabItem active={selectedTab === CreateEntityTypeTab.STAGES}>
            <button onClick={() => setSelectedTab(CreateEntityTypeTab.STAGES)}>
              Stages
            </button>
          </NavTabItem>
          <NavTabItem
            active={selectedTab === CreateEntityTypeTab.RELATIONSHIPS}
          >
            <button
              onClick={() => setSelectedTab(CreateEntityTypeTab.RELATIONSHIPS)}
            >
              Relationships
            </button>
          </NavTabItem>
        </NavTab>
        {tabContent}
      </div>
    </div>
  );
};

export default CreateEntityTypePage;
